import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client.ts';
import type { PlanListResponse } from '../bindings/PlanListResponse.ts';
import type { PlanDoc } from '../bindings/PlanDoc.ts';
import type { CreatePlanRequest } from '../bindings/CreatePlanRequest.ts';
import type { UpdatePlanRequest } from '../bindings/UpdatePlanRequest.ts';
import type { BlockContent } from '../bindings/BlockContent.ts';
import type { BlockResponse } from '../bindings/BlockResponse.ts';

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () => apiFetch<PlanListResponse>('/v1/plans'),
  });
}

export function usePlan(planId: string | undefined) {
  return useQuery({
    queryKey: ['plan', planId],
    queryFn: () => apiFetch<PlanDoc>(`/v1/plans/${planId}`),
    enabled: !!planId,
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: CreatePlanRequest) =>
      apiFetch<PlanDoc>('/v1/plans', { method: 'POST', body: JSON.stringify(req) }),
    onSuccess: (doc) => {
      queryClient.setQueryData(['plan', doc.plan.id], doc);
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}

export function useUpdatePlan(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdatePlanRequest) =>
      apiFetch<void>(`/v1/plans/${planId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}

export function useDeletePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => apiFetch<void>(`/v1/plans/${planId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plans'] }),
  });
}

export function useUpdateBlock(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, content }: { blockId: string; content: BlockContent }) =>
      apiFetch<BlockResponse>(`/v1/plans/${planId}/blocks/${blockId}`, {
        method: 'PATCH',
        body: JSON.stringify({ content }),
      }),
    onSuccess: (res) => {
      // patch the cached doc in place to avoid a refetch flash while editing
      queryClient.setQueryData<PlanDoc>(['plan', planId], (doc) =>
        doc
          ? {
              ...doc,
              sections: doc.sections.map((s) => ({
                ...s,
                blocks: s.blocks.map((b) => (b.id === res.block.id ? res.block : b)),
              })),
            }
          : doc,
      );
    },
  });
}

export function useAddBlock(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      content,
      position,
    }: {
      sectionId: string;
      content: BlockContent;
      position: number;
    }) =>
      apiFetch<BlockResponse>(`/v1/plans/${planId}/sections/${sectionId}/blocks`, {
        method: 'POST',
        body: JSON.stringify({ content, position }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plan', planId] }),
  });
}

export function useDeleteBlock(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (blockId: string) =>
      apiFetch<void>(`/v1/plans/${planId}/blocks/${blockId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plan', planId] }),
  });
}
